import React from 'react';
import Icon from 'components/AppIcon';

const KeyFindingsSection = ({ findings = [] }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'positive':
        return 'text-success bg-success-50 border-success-100';
      case 'concern':
        return 'text-error bg-error-50 border-error-100';
      case 'moderate':
        return 'text-warning bg-warning-50 border-warning-100';
      default:
        return 'text-text-secondary bg-surface-secondary border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'positive':
        return 'CheckCircle';
      case 'concern':
        return 'AlertTriangle';
      case 'moderate':
        return 'AlertCircle';
      default:
        return 'Circle';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'positive':
        return 'Strength';
      case 'concern':
        return 'Needs Attention';
      case 'moderate':
        return 'Monitor';
      default:
        return 'Neutral';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Icon name="TrendingUp" size={24} color="var(--color-primary)" />
        <h2 className="text-2xl font-heading font-semibold text-text-primary">
          Key Findings from Your Assessment
        </h2>
      </div>

      <p className="text-text-secondary">
        Based on your responses, here are the most important patterns and areas identified in your wellness assessment.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {findings.map((finding) => (
          <div key={finding.id} className="card hover:shadow-elevated transition-all duration-200">
            <div className="flex items-start space-x-4">
              <div className={`p-2 rounded-lg border ${getStatusColor(finding.status)}`}>
                <Icon 
                  name={getStatusIcon(finding.status)} 
                  size={20} 
                  color={
                    finding.status === 'positive' ? 'var(--color-success)' :
                    finding.status === 'concern' ? 'var(--color-error)' :
                    finding.status === 'moderate' ? 'var(--color-warning)' :
                    'var(--color-text-secondary)'
                  }
                />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h3 className="font-heading font-semibold text-text-primary">
                    {finding.category}
                  </h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(finding.status)}`}>
                    {getStatusLabel(finding.status)}
                  </span>
                </div>
                
                <p className="text-sm text-text-secondary mb-3">
                  {finding.finding}
                </p>
                
                <div className="bg-surface-secondary rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <Icon name="Lightbulb" size={14} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-text-secondary">
                      <span className="font-medium text-text-primary">Why this matters: </span>
                      {finding.impact}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="bg-surface-secondary rounded-lg p-6">
        <h3 className="font-heading font-semibold text-text-primary mb-4">
          Assessment Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-heading font-bold text-success mb-1">
              {findings.filter(f => f.status === 'positive').length}
            </div>
            <div className="text-sm text-text-secondary">Strengths Identified</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-heading font-bold text-warning mb-1">
              {findings.filter(f => f.status === 'moderate').length}
            </div>
            <div className="text-sm text-text-secondary">Areas to Monitor</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-heading font-bold text-error mb-1">
              {findings.filter(f => f.status === 'concern').length}
            </div>
            <div className="text-sm text-text-secondary">Priority Concerns</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyFindingsSection;